import React from "react";
import {ConfirmModal, useNotifications} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {api} from "@/data/api";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {Menu} from "@/data/entities/menu";
import {OptionGroup, ProductOption} from "@/data/entities/option";
import {ADMIN_MENU_QUERY_KEY} from "@/data/entities/menuEditor";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";

export type RemoveOptionState = {
    show: boolean,
    group: OptionGroup | null,
    option: ProductOption | null,
    otherGroupCount: number, // Groups the option stays in
};

type Props = {
    state: RemoveOptionState,
    setShow: (show: boolean) => void,
};

type RemoveVariables = {
    groupId: number,
    optionId: number,
};

/**
 * Removes an option from one option group. The option itself stays (also in
 * its other groups).
 */
const RemoveOptionModal = ({state, setShow}: Props) => {
    const intl = useIntl();
    const {notify} = useNotifications();
    const {dashboardLocation} = useRestaurant();
    const queryClient = useQueryClient();
    const queryKey = [ADMIN_MENU_QUERY_KEY, dashboardLocation.id];
    const {mutate, isPending} = useMutation({
        mutationFn: ({groupId, optionId}: RemoveVariables) =>
            api.menuEditor.removeOption(dashboardLocation.id, groupId, optionId),
        onSuccess: (_, {groupId, optionId}: RemoveVariables) => {
            // Remove it right away, then refetch to be sure
            queryClient.setQueryData<Menu>(queryKey, menu => menu === undefined ? menu : {
                ...menu,
                optionGroups: menu.optionGroups.map(group => group.id !== groupId ? group : {
                    ...group,
                    optionIds: group.optionIds.filter(id => id !== optionId),
                }),
            });
            void queryClient.invalidateQueries({queryKey});

            setShow(false);
            notifySuccess(intl.formatMessage({id: "deleted"}), notify);
        },
        onError: () => {
            notifyDanger(intl.formatMessage({id: "systemError"}), notify);
        },
    });

    return (
        <ConfirmModal
            type={"danger"}
            show={state.show}
            onClose={() => setShow(false)}
            title={intl.formatMessage({id: "removeOption"})}
            dismissButton={{
                text: intl.formatMessage({id: "cancel"}),
            }}
            continueButton={{
                text: intl.formatMessage({id: "delete"}),
                onClick: () => {
                    if (state.group !== null && state.option !== null) {
                        mutate({groupId: state.group.id, optionId: state.option.id});
                    }
                },
                props: {color: "danger"},
            }}
            disabled={isPending}
            loading={isPending}
            position={"center-bottom"}
            breakpoints={{md: {position: "center"}}}
        >
            <FormattedMessage
                id={"confirmRemoveOption"}
                values={{
                    option: <strong>{state.option?.name}</strong>,
                    group: <strong>{state.group?.name}</strong>,
                }}
            />
            {
                state.otherGroupCount > 0 &&
                <>
                    {" "}
                    <FormattedMessage id={"confirmRemoveOption.otherGroups"}/>
                </>
            }
        </ConfirmModal>
    );
};

export default RemoveOptionModal;